import { useMemo } from "react"

import { useTemplatesAll } from "@/templates/catalog"
import { Puzzle } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import type { FilterSectionProps } from "../../types/filters"

export const TemplateFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const { templateFilters, setTemplateFilters } = useRunsFilter()
  const {
    data: templatesData,
    isError,
    isLoading,
  } = useTemplatesAll({
    enabled: filterEnabled,
  })

  const items = useMemo(() => {
    const templates = templatesData?.templates || []
    return templates.map((template) => ({
      id: template.display_name,
      label: template.display_name,
    }))
  }, [templatesData?.templates])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: templateFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      width="w-72"
      disabled={isDisabled}
      emptyMessage="No templates available in this workspace."
      trigger={
        <FilterTrigger
          label="Templates"
          icon={<Puzzle className="w-4 h-4" />}
          isActive={templateFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={templateFilters}
        onSelectionChange={setTemplateFilters}
        isLoading={isLoading}
        isDeferred={filterEnabled === false}
        isOpen={isOpen}
        placeholder="Search templates..."
        emptyMessage="No templates found"
      />
    </FilterPopover>
  )
}
